import Papa from "papaparse";

import { appConfig } from "../config";
import type { SheetDerivedRow, SheetValue } from "./types";

type ParsedCell = string | number | boolean | null | undefined;
type ParsedCsvRow = ParsedCell[];

export function parseSheetCsv<T extends SheetDerivedRow = SheetDerivedRow>(
  raw: string,
): T[] {
  const { data } = Papa.parse<ParsedCsvRow>(raw);

  return parseSheetRows<T>(data);
}

export function parseSheetRows<T extends SheetDerivedRow = SheetDerivedRow>(
  data: ParsedCsvRow[],
): T[] {
  const stringData = data.map((row) => row.map(normalizeSheetCell));
  const keys = stringData[0] ?? [];

  return stringData
    .slice(1)
    .map((row, index) => toEntry(row, keys, index) as T);
}

function normalizeSheetCell(cell: ParsedCell): string {
  let value = String(cell ?? "");

  if (value.startsWith('"')) {
    value = value.substring(1, value.length - 1);
  }

  return value.trim();
}

function toEntry(
  row: string[],
  keys: string[],
  index: number,
): SheetDerivedRow {
  const ret: SheetDerivedRow = {};

  row.forEach((value, cellIndex) => {
    if (!value) return;

    const key = keys[cellIndex]?.trim();
    if (!key) return;

    ret[key] = inferValue(key, value.trim());
  });

  ret[appConfig.parserFields.rowOrder] = index;
  return ret;
}

function inferValue(key: string, value: string): SheetValue {
  if (isNumberField(key)) {
    return parseFloat(value);
  }

  if (key === appConfig.parserFields.date) {
    return new Date(Date.parse(value));
  }

  return value;
}

function isNumberField(key: string): boolean {
  return appConfig.parserFields.numeric.includes(
    key as (typeof appConfig.parserFields.numeric)[number],
  );
}
